import { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import client from "src/api/apollo/apolloClient";

const GET_ALL_ITINERARIES = gql`
  query (
    $name: String
    $createUid: Int
    $updateUid: Int
    $syncPelias: Boolean
    $routeCreated: Boolean
  ) {
    getItineraries(
      name: $name
      createUid: $createUid
      updateUid: $updateUid
      syncPelias: $syncPelias
      routeCreated: $routeCreated
    ) {
      id
      name
      description
      syncPelias
      routeCreated
      clientLoginId
      partnerLoginId
      createUid
      updateUid
      points {
        id
        name
        lat
        lng
        pointOrder
        placeId
        place {
          id
          name
          centroidLat
          centroidLon
          propertiesCategory
          propertiesSubcategory
          status
        }
      }
    }
  }
`;


const GET_ROUTE_OSM = gql`
  query ($input: [RouteCoordinateInput!]!) {
    getRouteDetails(input: $input) {
      trip {
        legs {
          shape {
            lat
            lon
          }
        }
      }
    }
  }
`;

const normalizeItineraryPoint = (point) => {
  const place = point?.place;
  const latitude = Number(point?.lat ?? place?.centroidLat);
  const longitude = Number(point?.lng ?? place?.centroidLon);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    id: point?.placeId ?? place?.id ?? point?.id ?? null,
    pointId: point?.id ?? null,
    placeId: point?.placeId ?? place?.id ?? null,
    name: point?.name || place?.name || "Point",
    latitude,
    longitude,
    metaData: "",
    pointOrder: Number(point?.pointOrder ?? 0),
    type: place?.propertiesSubcategory || place?.propertiesCategory || null,
    status: place?.status || null,
    place,
  };
};

const normalizeItinerary = (itinerary) => {
  const normalizedPoints = [...(itinerary?.points || [])]
    .sort(
      (first, second) =>
        Number(first?.pointOrder ?? 0) - Number(second?.pointOrder ?? 0),
    )
    .map(normalizeItineraryPoint)
    .filter(Boolean);

  const startLocation = normalizedPoints[0] || null;
  const endLocation = normalizedPoints[normalizedPoints.length - 1] || null;

  return {
    ...itinerary,
    startLocationId: startLocation?.id || null,
    endLocationId: endLocation?.id || null,
    startLocation,
    endLocation,
    stops: normalizedPoints.map((stop) => ({
      stopId: stop.id,
      position: stop.pointOrder,
      stop,
    })),
    stopPoints: normalizedPoints,
    points: normalizedPoints,
  };
};

const toWaypoint = (location) => {
  if (!location) return null;

  if (
    Number.isFinite(Number(location.latitude)) &&
    Number.isFinite(Number(location.longitude))
  ) {
    return {
      lat: Number(location.latitude),
      lon: Number(location.longitude),
      type: "break",
    };
  }

  return null;
};

export const useTravelHistory = (clientLoginId) => {
  const [tripData, setTripData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { data, loading: apiLoading, error: apiError } = useQuery(
    GET_ALL_ITINERARIES,
    {},
  );
  const routesTrips = useMemo(
    () => (data?.getItineraries || []).map(normalizeItinerary),
    [data],
  );

  useEffect(() => {
    setLoading(apiLoading);

    if (apiError) {
      setError(apiError.message);
      setTripData([]);
      setLoading(false);
      return;
    }

    const routes = routesTrips;
    if (!routes.length) {
      if (!apiLoading) {
        setTripData([]);
        setLoading(false);
      }
      return;
    }

    let cancelled = false;

    const processAllRoutes = async () => {
      try {
        const allRoutes = await Promise.all(
          routes.map(async (route) => {
            const waypoints = (route.points || [])
              .map(toWaypoint)
              .filter(Boolean);

            if (waypoints.length === 0) {
              return null;
            }

            let routePath = waypoints.map((wp) => ({ lat: wp.lat, lng: wp.lon }));

            if (waypoints.length >= 2) {
              try {
                const osmResponse = await client.query({
                  query: GET_ROUTE_OSM,
                  variables: {
                    input: waypoints,
                  },
                  fetchPolicy: "no-cache",
                });

                const legs = osmResponse?.data?.getRouteDetails?.trip?.legs || [];
                const tracedPath = [];

                for (let index = 0; index < legs.length; index += 1) {
                  const steps = legs[index]?.shape || [];
                  for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
                    tracedPath.push({
                      lat: steps[stepIndex].lat,
                      lng: steps[stepIndex].lon,
                    });
                  }
                }

                if (tracedPath.length > 0) {
                  routePath = tracedPath;
                }
              } catch (osmError) {
                console.warn(
                  `OSRM API failed for route ${route.id}, using direct path:`,
                  osmError,
                );
              }
            }

            return {
              routeId: route.id,
              routeName: route.name,
              path: routePath,
              waypoints,
              distanceKm: route.avgTripDistance || 0,
              startPoint: routePath[0] || null,
              endPoint: routePath[routePath.length - 1] || null,
              totalPoints: routePath.length,
              startLocation: route.startLocation,
              endLocation: route.endLocation,
              stops: route.stops,
              rawRoute: route,
            };
          }),
        );

        if (cancelled) return;

        const validRoutes = allRoutes.filter((route) => route?.path?.length > 0);
        setTripData(validRoutes);
        setError(validRoutes.length > 0 ? null : "Aucun point de trajet trouve");
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error("Erreur lors du traitement des routes:", err);
        setError(err.message);
        setTripData([]);
        setLoading(false);
      }
    };

    processAllRoutes();

    return () => {
      cancelled = true;
    };
  }, [routesTrips, apiLoading, apiError]);

  return {
    routesTrips,
    tripData,
    loading,
    error,
  };
};
